import React, { useEffect, useRef, useState } from "react";

import * as Sentry from "@sentry/react";
import { useStatsigClient } from "@statsig/react-bindings";
import { isEmpty } from "lodash/fp";
import { useNavigate } from "react-router";
import { Outlet, useLocation } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

import AuthRSS from "@app/analytics/assets/auth/auth_rss.png";
import UpsellContent from "@app/analytics/auth/AuthLanding/UpsellContent";
import Tabs from "@app/analytics/components/Tabs";
import { EXPERIMENT_NAMES } from "@app/analytics/experiments/constants";
import ExperimentProvider from "@app/analytics/experiments/ExperimentProvider";
import useApps from "@app/common/hooks/useApps";
import usePreloadImages from "@app/common/hooks/usePreloadImages";
import ROUTES from "@app/common/routes";
import { LocationWithState } from "@app/common/types";
import logger from "@app/utils/logger";

import FullPageLoadingSpinnerWithText from "@rui/FullPageLoadingSpinnerWithText";
import ReadLogo from "@rui/ReadLogo";
import Stack, { Row } from "@rui/Stack";

import AuthLandingCarousel, {
  VideoPanel,
  ImagePanel,
} from "./AuthLandingCarousel";
import AuthLandingWithReport from "./AuthLandingWithReport";
import {
  FormColumn,
  FormColumnContent,
  LeftColumn,
  ParentContainer,
  RightColumn,
  WideColumnContent,
  TAB_STYLE_OVERRIDES,
} from "./styles";

const ALL_IMAGES = [AuthRSS];
const EMPTY_PANELS: ImagePanel[] = [];

const AUTH_TABS = [
  {
    id: "signup",
    label: "Create account",
    path: ROUTES.SIGN_UP,
    enabled: true,
  },
  {
    id: "signin",
    label: "Sign in",
    path: ROUTES.SIGN_IN,
    enabled: true,
  },
];

const SIGN_UP_PANELS: VideoPanel[] = [
  {
    video:
      "https://readai-assets-production.s3.amazonaws.com/readai_videos/Read_Meeting+Notes.mp4",
    caption: "Automatic meeting summaries",
  },
  {
    video:
      "https://readai-assets-production.s3.amazonaws.com/readai_videos/Read_Transcription.mp4",
    caption: "Transcription augmented with reactions",
  },
  {
    video:
      "https://readai-assets-production.s3.amazonaws.com/readai_videos/Read_VideoPlayback.mp4",
    caption: "Video playback with AI highlights",
  },
];

const RSS_SIDE_PANELS: ImagePanel[] = [
  {
    image: AuthRSS,
    caption: "Schedule smarter with Read",
  },
];

const AuthLanding = () => {
  usePreloadImages(ALL_IMAGES);

  const { client } = useApps();
  const location = useLocation() as LocationWithState;
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const source = queryParams.get("source");
  const zoomRedirect = queryParams.get("zoomRedirect");
  const redirectTo = queryParams.get("redirectTo");
  const isEmbeddedAuth = client !== "browser";

  const onSignIn = location.pathname.includes("signin");

  const panels =
    source === "rss"
      ? RSS_SIDE_PANELS
      : onSignIn
        ? EMPTY_PANELS
        : SIGN_UP_PANELS;

  const handleAuthTabChange = (path: string) => {
    const stateRedirectTo =
      zoomRedirect || redirectTo
        ? { redirectTo: { pathname: zoomRedirect || redirectTo } }
        : location.state?.redirectTo;

    navigate(
      { pathname: path, search: location.search },
      {
        state: {
          ...location.state,
          redirectTo: stateRedirectTo,
        },
      }
    );
  };

  return (
    <ParentContainer>
      <LeftColumn
        flex={isEmbeddedAuth ? 1 : undefined}
        justifyContent={isEmbeddedAuth ? "flex-start !important" : undefined}
      >
        <FormColumn justifyContent={isEmbeddedAuth ? "flex-start" : "center"}>
          <FormColumnContent variant="apart">
            <Stack variant="center" spacing={3} flex={1} width="100%">
              <Stack width="100%" maxWidth="390px">
                <ReadLogo sx={{ mb: "28px" }} />
                <Row>
                  <Tabs
                    sx={TAB_STYLE_OVERRIDES}
                    tabs={AUTH_TABS}
                    onChange={handleAuthTabChange}
                    variant="fullWidth"
                  />
                </Row>
              </Stack>
              <Outlet />
            </Stack>
          </FormColumnContent>
        </FormColumn>
      </LeftColumn>
      {!isEmbeddedAuth && (
        <RightColumn>
          <WideColumnContent>
            {isEmpty(panels) ? (
              <UpsellContent />
            ) : (
              <AuthLandingCarousel panels={panels} />
            )}
          </WideColumnContent>
        </RightColumn>
      )}
    </ParentContainer>
  );
};

const AuthLandingGate = () => {
  const [queryParams] = useSearchParams();
  const location = useLocation();

  const { client } = useStatsigClient();

  const forceExperiment = queryParams.get("forceExperiment") === "true";
  const isSignupPage = location.pathname.includes("signup");
  const hasSession = queryParams.get("sessionId");

  // use a ref to avoid this value changing if for example, the user first went to
  // the sign in page and then navigated to the sign up page
  const experimentCandidate = useRef(
    isSignupPage && hasSession && !forceExperiment
  ).current;

  const [inExperiment, setInExperiment] = useState(forceExperiment);
  const [isLoading, setIsLoading] = useState(experimentCandidate);

  useEffect(() => {
    if (!experimentCandidate) {
      // fail safe
      setIsLoading(false);
      return;
    }

    client
      .initializeAsync()
      .then(() => {
        setInExperiment(
          client
            .getExperiment(EXPERIMENT_NAMES.reportSignup)
            .get("in_experiment", false)
        );
      })
      .catch((err) => {
        logger.error(err);
        Sentry.captureException(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [experimentCandidate]);

  if (isLoading) {
    return <FullPageLoadingSpinnerWithText />;
  }

  if (inExperiment) {
    return <AuthLandingWithReport />;
  }

  return <AuthLanding />;
};

const AuthLandingExperiment = () => {
  return (
    <ExperimentProvider>
      <AuthLandingGate />
    </ExperimentProvider>
  );
};

export default AuthLandingExperiment;
